import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { catchError, distinctUntilChanged, map, of, skip, startWith, switchMap, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { get, isEqual } from 'lodash-es';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { DateHelperService } from '../../services/date-helper.service';
import { debugLog } from 'app/shared/utils/debug-logger';
import { calculateLeaveHours } from './date-hours-calculator.utils';

const BOUNDARY_KEYS = ['startDate', 'endDate'] as const;

@Component({
  selector: 'formly-field-date-hours-calculator',
  template: `
    <div>
      <h3 class="mb-2">
        {{ props.label }}
        <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
      </h3>
      <div class="block">
        @for (field of field.fieldGroup; track $index) {
          <formly-field [field]="field" [class]="field.className"></formly-field>
        }
      </div>
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormlyModule, MatInputModule, MatDatepickerModule, InfoTooltipComponent],
})
export class FormlyFieldDateHoursCalculator extends FieldType<FieldTypeConfig> implements OnInit {
  readonly #entriesService = inject(EntriesService);
  readonly #modulesStore = inject(ModulesStore);
  readonly #destroy$ = injectDestroy();

  /** Contracted hours per week of the selected employee; null = unknown, so 40 is assumed. */
  #maxHoursPerWeek: number | null = null;

  ngOnInit() {
    this.#initWholeDays();
    this.#showTimes(!this.formControl.value?.wholeDays);

    // Recalculate only when an INPUT of the calculation changes — never on an edit of `hours`
    // itself, so a manually corrected total stays put. The initial value is skipped: opening a
    // saved request must not rewrite the hours it was saved with.
    this.formControl.valueChanges
      .pipe(
        startWith(this.formControl.value),
        map(value => ({ startDate: value?.startDate, endDate: value?.endDate, wholeDays: !!value?.wholeDays })),
        distinctUntilChanged(isEqual),
        skip(1),
        takeUntil(this.#destroy$)
      )
      .subscribe(({ wholeDays }) => {
        if (wholeDays) {
          this.#dropTimes();
        }
        this.#showTimes(!wholeDays);
        this.#recalculate();
      });

    this.#watchContract();
  }

  /**
   * A new request starts on whole days. A request saved before the toggle existed has no value:
   * it was entered with times unless both boundaries are plain dates, so keep showing it that way.
   */
  #initWholeDays(): void {
    const control = this.formControl.get('wholeDays');
    if (!control || control.value != null) {
      return;
    }
    const { startDate, endDate } = this.formControl.value ?? {};
    const dateOnly = (value: unknown) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    control.setValue(!startDate || (dateOnly(startDate) && dateOnly(endDate)));
  }

  #recalculate(): void {
    const { startDate, endDate, wholeDays } = this.formControl.value ?? {};
    const hours = this.formControl.get('hours');
    if (!hours || !startDate || !endDate) {
      return;
    }
    // Emitting on purpose: Formly copies a control's value into the model on valueChanges, and the
    // saved entry is built from the model. A silent patch showed 8 but saved `hours: null`, which
    // then left the required field empty on the detail page. The recalculation pipeline above
    // ignores changes to `hours`, so this cannot loop.
    hours.patchValue(
      calculateLeaveHours(startDate, endDate, { wholeDays: !!wholeDays, maxHoursPerWeek: this.#maxHoursPerWeek })
    );
  }

  /** Whole days: a time entered earlier means nothing anymore, so the boundaries become plain dates. */
  #dropTimes(): void {
    BOUNDARY_KEYS.forEach(key => {
      const control = this.formControl.get(key);
      const { date } = DateHelperService.dateTimeToDateAndTime(control?.value ?? null);
      if (control && date && date !== control.value) {
        // Emitting, for the same model sync as in #recalculate.
        control.setValue(date);
      }
    });
  }

  #showTimes(show: boolean): void {
    this.field.fieldGroup
      ?.filter(subField => (BOUNDARY_KEYS as readonly unknown[]).includes(subField.key))
      .forEach((subField: FormlyFieldConfig) => {
        subField.props = subField.props ?? {};
        subField.props['hideTime'] = !show;
        // `hideTime` is set after the datetime field started observing its props (ngx-formly
        // observeDeep), so it has to be told; see route.component for the same case.
        subField.options?.detectChanges?.(subField);
      });
  }

  /**
   * Caps the total at the employee's contracted hours per week. Configured per field: which form
   * field holds the employee (`contractUserFieldPath`) and where the user entry keeps the weekly
   * hours (`contractHoursKeyPath`). Without either, or when the user cannot read that field, 40
   * hours per week is assumed. The first lookup does not recalculate a saved request; a later employee change does.
   */
  #watchContract(): void {
    const userPath = this.props['contractUserFieldPath'] as string | undefined;
    const hoursPath = this.props['contractHoursKeyPath'] as string | undefined;
    if (!userPath || !hoursPath) {
      return;
    }
    // The root: `this.form` is only the parent group when the calculator sits in a keyed group.
    const userControl = this.form.root.get(userPath.replace(/^entry\./, ''));
    if (!userControl) {
      debugLog('DateHoursCalculator', 'contract user field not found, assuming 40 hours', { userPath });
      return;
    }

    const usersModuleId = this.#modulesStore.moduleCodeToId('users');
    let first = true;
    userControl.valueChanges
      .pipe(
        startWith(userControl.value),
        map(value => this.#userId(value)),
        distinctUntilChanged(),
        switchMap(userId =>
          userId && usersModuleId
            ? this.#entriesService.getEntry(usersModuleId, userId).pipe(
                map(entry => {
                  const weekly = Number(get(entry, hoursPath));
                  return weekly > 0 ? weekly : null;
                }),
                catchError(error => {
                  debugLog('DateHoursCalculator', 'contract hours lookup failed', { userId, error });
                  return of(null);
                })
              )
            : of(null)
        ),
        takeUntil(this.#destroy$)
      )
      .subscribe(maxHoursPerWeek => {
        this.#maxHoursPerWeek = maxHoursPerWeek;
        if (!first || this.#isEdited(userControl)) {
          this.#recalculate();
        }
        first = false;
      });
  }

  #isEdited(userControl: AbstractControl): boolean {
    return userControl.dirty || BOUNDARY_KEYS.some(key => this.formControl.get(key)?.dirty);
  }

  #userId(value: unknown): string | null {
    const user = Array.isArray(value) ? value[0] : value;
    if (!user || typeof user !== 'object') {
      return null;
    }
    const { id, entryId } = user as { id?: string; entryId?: string };
    return id ?? entryId ?? null;
  }
}
